import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, BriefcaseIcon, CheckCircle2Icon, GraduationCapIcon, HelpCircleIcon, HomeIcon, MapPinIcon, MessageSquareIcon, SunIcon, TimerIcon } from 'lucide-react';
import { useContactWizard } from '../../hooks/useContactWizard';
import { contactTopics, stepLabels } from '../../data/contactTopics';
import type { ContactTopicId } from '../../data/contactTopics';
import { priceLists } from '../../data/priceLists';
import { ContactDetailsForm } from './ContactDetailsForm';
import { ContactSummary } from './ContactSummary';
const topicIcons: Record<ContactTopicId, React.ComponentType<{
  className?: string;
}>> = {
  english: GraduationCapIcon,
  young: SunIcon,
  professional: BriefcaseIcon,
  short: TimerIcon,
  visa: HomeIcon,
  student: MessageSquareIcon,
  other: HelpCircleIcon
};
const ease = [0.23, 1, 0.32, 1] as const;

// Dynamic enquiry flow: each answer narrows the next question to what ES World actually offers.
export function ContactWizard() {
  const w = useContactWizard();
  const reduce = useReducedMotion();
  const motionProps = reduce ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      x: 16
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -16
    }
  };
  return <div className="grid gap-8 lg:grid-cols-12 lg:gap-10" data-id="element-169" data-mp="2k355vhxk1rz3">
      <div className="rounded-[28px] bg-white p-6 md:p-10 lg:col-span-8" data-id="element-170" data-mp="37wdjx4esxjp7">
        {w.status === 'success' ? <div role="status" className="flex flex-col items-start py-6" data-id="element-171" data-mp="3ctna4ma0lze3">
            <CheckCircle2Icon className="h-12 w-12 text-success" aria-hidden="true" data-id="element-172" data-mp="2ytcg5ades4op" />
            <h2 className="mt-5 type-h3 text-ink" data-id="element-173" data-mp="32v7r76l55wfo">Thank you, {w.contact.name.split(' ')[0]}</h2>
            <p className="mt-3 max-w-xl type-body text-body" data-id="element-174" data-mp="1nepfjjmmfekx">
              Your message about {w.course?.name ?? w.topic?.label.toLowerCase()} is with our team. We will reply to {w.contact.email}
              {w.contact.phone ? ' and send you a WhatsApp message' : ''}, usually within one working day.
            </p>
            <button type="button" onClick={w.restart} className="mt-8 inline-flex h-12 items-center rounded-[15px] border-2 border-brand px-6 type-button font-bold text-ink transition-colors duration-150 hover:bg-cream" data-id="element-175" data-mp="3p5fx3ch7kt7y">
              Send another enquiry
            </button>
          </div> : <>
            <nav aria-label="Enquiry progress" className="mb-8" data-id="element-176" data-mp="36ddkcc7otx2z">
              <ol className="flex gap-2" data-id="element-177" data-mp="36g9km770cxul">
                {w.steps.map((s, i) => {
              const done = i < w.stepIndex;
              const current = i === w.stepIndex;
              return <li key={s} className="flex-1" data-id="element-178" data-mp="zngxdxu8yxju" data-mpi={i}>
                      <button type="button" onClick={() => w.goTo(s)} disabled={i > w.stepIndex} aria-current={current ? 'step' : undefined} className="group block w-full text-left disabled:cursor-default" data-id="element-179" data-mp="zmxby75e1r12">
                        <span className={`block h-1.5 rounded-full transition-colors duration-200 ${done || current ? 'bg-brand' : 'bg-line'}`} data-id="element-180" data-mp="1bxyfekw9edl0" />
                        <span className={`mt-2 hidden type-small sm:block ${current ? 'font-bold text-ink' : done ? 'text-body group-hover:text-ink' : 'text-muted'}`} data-id="element-181" data-mp="1gw9ouuin6m2p">
                          {stepLabels[s]}
                        </span>
                      </button>
                    </li>;
            })}
              </ol>
            </nav>

            {w.stepIndex > 0 ? <button type="button" onClick={w.back} className="mb-4 inline-flex items-center gap-1.5 type-small font-semibold text-body transition-colors duration-150 hover:text-ink" data-id="element-182" data-mp="y7uxg64ex61d">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-183" data-mp="29k5hno53btu7" />
                Back
              </button> : null}

            <AnimatePresence mode="wait" initial={false} data-id="element-184" data-mp="3ik8bsql84xlm">
              <motion.div key={w.step} {...motionProps} transition={{
            duration: 0.2,
            ease
          }} data-id="element-185" data-mp="3dklykh5ebhct">
                {w.step === 'topic' ? <StepHeading title="What can we help you with?" text="Choose a topic. We will only ask what matters for it." data-id="element-186" data-mp="3rgz730ryc8f5" /> : null}
                {w.step === 'topic' ? <ul className="grid gap-3 sm:grid-cols-2" data-id="element-187" data-mp="59r23zky23jn">
                    {contactTopics.map(t => {
                const Icon = topicIcons[t.id];
                const selected = w.topic?.id === t.id;
                return <li key={t.id} data-id="element-188" data-mp="a3mzp0xapoea">
                          <button type="button" onClick={() => w.chooseTopic(t.id)} aria-pressed={selected} className={`group flex h-full w-full items-start gap-4 rounded-[20px] border-2 p-5 text-left transition-colors duration-150 ${selected ? 'border-brand bg-cream' : 'border-line hover:border-brand hover:bg-cream'}`} data-id="element-189" data-mp="a4ok3w07bn8o">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-brand text-white" aria-hidden="true" data-id="element-190" data-mp="xpp73r00wi3a">
                              <Icon className="h-5 w-5" data-id="element-191" data-mp="jwzm9pw8dj7r" />
                            </span>
                            <span className="min-w-0" data-id="element-192" data-mp="oq2dhrdjcgks">
                              <span className="block type-card-title text-ink" data-id="element-193" data-mp="a0mtcu8if9e2">{t.label}</span>
                              <span className="mt-1 block type-small text-body" data-id="element-194" data-mp="ez7tyurobv31">{t.text}</span>
                            </span>
                          </button>
                        </li>;
              })}
                  </ul> : null}

                {w.step === 'course' && w.topic ? <>
                    <StepHeading title="Which programme?" text={`${w.courseOptions.length} ${w.topic.label.toLowerCase()} options. Not sure? Pick the closest — we will advise you.`} data-id="element-195" data-mp="27p9s1de3mh5s" />
                    <ul className="grid gap-3 sm:grid-cols-2" data-id="element-196" data-mp="1xyxc5yh8bc5z">
                      {w.courseOptions.map(c => {
                  const selected = w.course?.slug === c.slug;
                  return <li key={c.slug} data-id="element-197" data-mp="2qm1hy5o5buit">
                            <button type="button" onClick={() => w.chooseCourse(c.slug)} aria-pressed={selected} className={`group flex h-full w-full flex-col rounded-[20px] border-2 p-5 text-left transition-colors duration-150 ${selected ? 'border-brand bg-cream' : 'border-line hover:border-brand hover:bg-cream'}`} data-id="element-198" data-mp="2ln87km45ojmv">
                              <span className="flex items-start justify-between gap-3" data-id="element-199" data-mp="3cwjhqa8dpim2">
                                <span className="type-card-title text-ink" data-id="element-200" data-mp="3gxv4m7zvbpqk">{c.name}</span>
                                <ArrowRightIcon className="mt-1 h-4 w-4 shrink-0 text-brand-press transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-201" data-mp="3luvektzu9cmy" />
                              </span>
                              <span className="mt-1 type-small text-body" data-id="element-202" data-mp="3vky7ku6ymqla">{c.cardText}</span>
                              <span className="mt-auto pt-3 type-small text-muted" data-id="element-203" data-mp="4e3qxnzrvdj3">
                                {c.audience} · {c.locations.length} {c.locations.length === 1 ? 'location' : 'locations'}
                              </span>
                            </button>
                          </li>;
                })}
                    </ul>
                  </> : null}

                {w.step === 'location' ? <>
                    <StepHeading title={w.topic?.id === 'student' ? 'Which campus are you at?' : 'Where would you like to study?'} text={w.course ? `${w.course.name} runs at these locations.` : 'Choose the campus your question is about.'} data-id="element-204" data-mp="kykp98own0pk" />
                    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-205" data-mp="prngvba4g034">
                      {w.locationOptions.map(d => {
                  const selected = w.location?.slug === d.slug;
                  const from = w.course?.group === 'english' ? priceLists.find(l => l.slug === d.priceList)?.fromPrice : undefined;
                  return <li key={d.slug} data-id="element-206" data-mp="6zl80m3q1o3f">
                            <button type="button" onClick={() => w.chooseLocation(d.slug)} aria-pressed={selected} className={`group block w-full overflow-hidden rounded-[20px] border-2 text-left transition-colors duration-150 ${selected ? 'border-brand' : 'border-line hover:border-brand'}`} data-id="element-207" data-mp="71xosvxqtsew">
                              <img src={d.image} alt="" className="aspect-[16/9] w-full object-cover" data-id="element-208" data-mp="2reb2rfan7hym" />
                              <span className="block p-4" data-id="element-209" data-mp="2wcw27ci2lxm0">
                                <span className="block type-card-title text-ink" data-id="element-210" data-mp="3k1x4ei0b8ciu">{d.name}</span>
                                <span className="mt-0.5 flex items-center gap-1.5 type-small text-muted" data-id="element-211" data-mp="3o0xmfyayrb4s">
                                  <MapPinIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-212" data-mp="3aaaxgxv08zlw" />
                                  {d.country}
                                </span>
                                {from ? <span className="mt-2 block type-small font-bold text-ink" data-id="element-213" data-mp="6tbzrkyn5coy">From {from} / week</span> : null}
                              </span>
                            </button>
                          </li>;
                })}
                    </ul>
                    {w.topic?.id !== 'student' ? <button type="button" onClick={() => w.chooseLocation('')} className="mt-5 type-button font-bold text-brand-press underline underline-offset-4 transition-colors duration-150 hover:text-ink" data-id="element-214" data-mp="1u2hfeg2n7atp">
                        I&apos;m not sure yet
                      </button> : null}
                  </> : null}

                {w.step === 'details' && w.topic ? <>
                    <StepHeading title="Almost done" text="Tell us a little more so we can reply with the right answer the first time." data-id="element-215" data-mp="1fect724dsh66" />
                    <ContactDetailsForm topic={w.topic} answers={w.answers} contact={w.contact} errors={w.errors} submitting={w.status === 'submitting'} failed={w.status === 'error'} onAnswer={w.answer} onContact={(key, value) => w.updateContact(key, value as never)} onSubmit={w.submit} data-id="element-216" data-mp="11fmuxj5tsbg8" />
                  </> : null}
              </motion.div>
            </AnimatePresence>
          </>}
      </div>

      <div className="lg:col-span-4" data-id="element-217" data-mp="10r4evsfrzvpz">
        <ContactSummary topic={w.topic} course={w.course} location={w.location} data-id="element-218" data-mp="10q2zxcvn2p2z" />
      </div>
    </div>;
}
function StepHeading({
  title,
  text
}: {
  title: string;
  text: string;
}) {
  return <div className="mb-6" data-id="element-219" data-mp="1agxpor83g6t6">
      <h2 className="type-h3 text-ink" data-id="element-220" data-mp="1airyj763c8gw">{title}</h2>
      <p className="mt-2 type-body text-body" data-id="element-221" data-mp="1oh8hbwh89pfe">{text}</p>
    </div>;
}